<template>
  <div v-if="!isResult" class="app-form">
    <div class="app-form-body">
      <div class="app-form-banner" v-if="data.bannerImage || data.bannerImage">
        <Image
          width="100%"
          height="68"
          fit="cover"
          :src="data.bannerPath ? data.bannerPath : data.bannerImage"
        />
      </div>
      <div class="app-form-title" v-if="data.titleName">{{ data.titleName }}</div>
      <div class="app-form-subtitle" v-if="data.subTitleName">{{ data.subTitleName }}</div>
      <Form ref="formRef" label-align="top" class="form-class">
        <div v-for="(item, index) in data.filedList" :key="index">
          <div v-if="item.fieldType === '11'" class="explaination">{{ item.fieldName }}</div>
          <div v-else>
            <Field
              :label="item.fieldName"
              :name="item.fieldName"
              v-show="item.isShow"
              :required="item.requiredFlag === '1'"
              :validate-trigger="trigger(item)"
              :rules="[
                {
                  validator: (rule, value, callback) => validator(rule, value, callback, item)
                }
              ]"
            >
              <template #input>
                <!-- 1：单行文本 ， 2：多行文本 -->
                <Field
                  v-if="['1', '2'].includes(item.fieldType)"
                  :class="{ 'field-class': item.fieldType == 1 }"
                  v-model="item.fieldValue"
                  :show-word-limit="item.fieldType == 2"
                  :maxlength="item.characterLimit ? item.characterLimit : 1000"
                  :type="item.fieldType === '2' ? 'textarea' : 'text'"
                  :placeholder="item.remind ? item.remind : $t('ep_admin_tips_input')"
                  :formatter="e => formatter(e, item)"
                  :disabled="
                    disabled || (item.systemType === '7' && routeData.templateType === '1')
                  "
                  @blur="inputBlur(item)"
                />
                <!-- 3：单选 -->
                <HRadio
                  v-if="['3'].includes(item.fieldType)"
                  :data="item"
                  :disabled="disabled"
                  @change="e => selectChange(e, item)"
                />
                <!-- 4：下拉单选 -->
                <HSelect
                  v-if="['4'].includes(item.fieldType) && !item.systemType"
                  :data="item"
                  :disabled="disabled"
                  @change="e => selectChange(e, item)"
                />
                <HCountryStateSelect
                  v-if="['4'].includes(item.fieldType) && item.systemType"
                  :data="item"
                  :disabled="disabled"
                  :countryValue="countryValue"
                  v-model:value="item.fieldValue"
                  @change="countryChange"
                />
                <!-- 5：多选 -->
                <HCheckbox
                  v-if="['5'].includes(item.fieldType)"
                  :data="item"
                  :disabled="disabled"
                  @change="e => selectChange(e, item)"
                />
                <!-- 6：日期选择 -->
                <HDatePicker
                  v-if="['6'].includes(item.fieldType)"
                  :data="item"
                  :disabled="disabled"
                  @change="e => selectChange(e, item)"
                />
                <!-- 7：评分 -->
                <Rate
                  v-if="['7'].includes(item.fieldType)"
                  v-model="item.fieldValue"
                  void-icon="star"
                  void-color="rgb(231, 231, 231)"
                  color="rgb(255, 166, 0)"
                  :size="28"
                  :gutter="0"
                  :disabled="disabled"
                  :allow-half="initData.alignment !== '2'"
                />
                <!-- 8：矩阵评分 -->
                <HMatrixScore
                  v-if="['8'].includes(item.fieldType)"
                  :data="item"
                  :disabled="disabled"
                  :allowHalf="initData.alignment !== '2'"
                  @updataMatrixScore="e => selectChange(e, item)"
                />
                <Sort
                  v-if="['12'].includes(item.fieldType)"
                  :data="item"
                  :disabled="disabled"
                  @change="e => selectChange(e, item)"
                />
              </template>
            </Field>
          </div>
        </div>
      </Form>
      <div class="disclaimer-class" v-if="data.disclaimer">{{ data.disclaimer }}</div>
    </div>
    <div class="app-form-fotter">
      <Form ref="agreementRef" label-align="top" class="form-agreement">
        <Field
          name="agreementChecked"
          :required="true"
          validate-trigger="change"
          :rules="[
            {
              validator: (rule, value, callback) => validatorAgreement(rule, value, callback)
            }
          ]"
        >
          <template #input>
            <div class="agreement-class">
              <Checkbox v-model="agreementChecked" checked-color="rgb(215, 0, 15)"
                ><span style="color: rgba(0, 0, 0, 0.85)">{{ data.agreementContent }}</span>
              </Checkbox>
              <span style="color: rgb(33, 150, 243)" @click="agreementLink">{{
                data.agreementUrl
              }}</span>
            </div>
          </template>
        </Field>
      </Form>
      <Button
        type="primary"
        class="submit-button"
        @click="submit"
        :disabled="disabled"
        :loading="loading"
        >{{ data.submitButtonLanguage }}</Button
      >
    </div>
  </div>
  <Result v-else :resultData="initData" />
  <ActionSheet v-model:show="showPolicy" round position="bottom" :title="data.agreementUrl">
    <div class="policy-class" style="padding: 16px; text-align: left" v-html="policy"></div>
    <template #cancel>
      <Button style="width: 100%" @click="showPolicy = false" round>{{
        $t('ep_admin_apiLog_close')
      }}</Button>
    </template>
  </ActionSheet>
</template>
<script setup lang="ts">
import { i18nt } from '@/i18n'
import { ref, onMounted, watch } from 'vue'
import HRadio from './HRadio.vue'
import HSelect from './HSelect.vue'
import HCountryStateSelect from './HCountryStateSelect.vue'
import HCheckbox from './HCheckbox.vue'
import HDatePicker from './HDatePicker.vue'
import HMatrixScore from './HMatrixScore.vue'
import Sort from './Sort.vue'
import Result from './Result.vue'
import { Image, Button, Form, Field, Rate, Checkbox, ActionSheet, Locale } from 'vant'
import { FormSubmit, EventSubmit } from '@/services/marketing-management/form-management'
import cloneDeep from 'lodash/cloneDeep'
import { policy } from './policy'
// 引入英文语言包
import enUS from 'vant/es/locale/lang/en-US.mjs'

Locale.use('en-US', enUS)
const props = defineProps({
  initData: {
    type: Object,
    required: true,
    default: () => ({})
  },
  routeData: {
    type: Object,
    required: true,
    default: () => ({})
  },
  disabled: {
    required: true,
    type: Boolean,
    default: false
  },
  isPreview: {
    required: true,
    type: Boolean,
    default: false
  }
})
const data = ref<any>({})
const formRef = ref(null)
const agreementRef = ref(null)
const logicConfigList = ref([])
const isResult = ref(false)
const showPolicy = ref(false)
const countryValue = ref<string | undefined>(undefined)
const loading = ref(false)
const agreementChecked = ref(false)
const startTime = ref(0)

const formatter = (value: any, item: any) => {
  // 开启输入数字后限制只能输入数字且只有一个负号和一个小数点。其中负号只能在数字前，小数点只能在数字后。
  if (item.numberTypeFlag === '1') {
    return value.replace(/[^0-9.-]|(?<=\..*)\.|(?<!\d)\.|(?!^)-/g, '')
  }
  return value
}
// 输入后清空校验
const inputBlur = (item: any) => {
  formRef.value.validate(item.fieldName)
}

const trigger = (item: any) => {
  switch (item.fieldType) {
    case '1':
    case '2':
    case '8':
    case '9':
    case '10':
      return 'onBlur'
    case '3':
    case '4':
    case '5':
    case '6':
    case '7':
    case '12':
      return ['onChange', 'onBlur']
  }
}
// 矩阵打分必填校验判断
const matrixScoreValidate = (item: any) => {
  if (item.fieldValue && item.fieldValue.length > 0) {
    const result = item.fieldValue.some(item => {
      return item.column.some(i => i.score === 0)
    })
    return !result
  }
  return false
}
// 必填校验
const validatorAgreement = (rule, value, callback) => {
  if (!agreementChecked.value) {
    return i18nt('ep_admin_tips_select')
  }
  return true
}
const validator = (rule, value, callback, item) => {
  if (
    item.isShow &&
    ['3', '4', '5', '12'].includes(item.fieldType) &&
    !item.systemType &&
    item.fieldValue &&
    item.fieldValue.length > 0
  ) {
    const result = item.fieldValue.some(item => item.othersFlag && !item.otherContent)
    if (result) return i18nt('ep_admin_tips_input')
  }
  if (item.requiredFlag === '1' && item.isShow && item.systemType === '7') {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    if (regex.test(item.fieldValue)) {
      return true
    } else {
      return i18nt('ep_admin_form_emailTips')
    }
  }
  if (item.requiredFlag === '1' && item.isShow && ['1', '2'].includes(item.systemType)) {
    return item.fieldValue ? true : i18nt('ep_admin_tips_select')
  }
  if (item.requiredFlag === '1' && item.isShow) {
    switch (item.fieldType) {
      case '1':
      case '2':
        return item.fieldValue ? true : i18nt('ep_admin_tips_input')
      case '3':
      case '4':
      case '5':
      case '12':
        return item.fieldValue?.length > 0 ? true : i18nt('ep_admin_tips_select')
      case '6':
      case '7':
        return item.fieldValue ? true : i18nt('ep_admin_tips_select')
      case '8':
        return matrixScoreValidate(item) ? true : i18nt('ep_admin_tips_select')
      // case '9':
      // case '10':
      //   return
    }
  } else {
    return true
  }
}
// 选择数据调整
const selectChange = async (e: any, item: any) => {
  item.fieldValue = e
  if (item.isShow && ['3', '4', '5', '12'].includes(item.fieldType) && item.fieldValue.length > 0) {
    return formRef.value.validate(item.fieldName)
  }
  if ((item.fieldType === '4' || item.fieldType === '6') && item.requiredFlag === '1') {
    formRef.value.validate(item.fieldName)
  }
  if (item.fieldType === '8' && matrixScoreValidate(item)) {
    formRef.value.validate(item.fieldName)
  }
}

const countryChange = (val: any) => {
  countryValue.value = val
}

const containsList = (arr1, arr2) => {
  return arr2.some(item => arr1.includes(item))
}

const agreementLink = () => {
  showPolicy.value = true
}

// 生成唯一uid
const getRandomNumber = () => {
  let randomNumber = localStorage.getItem('formUid')
  if (!randomNumber) {
    randomNumber = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0,
        v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
    localStorage.setItem('formUid', randomNumber)
  }
  return randomNumber
}

const submit = async () => {
  await formRef.value.validate()
  await agreementRef.value.validate()
  if (loading.value) return
  try {
    loading.value = true
    let filedList = cloneDeep(
      data.value.filedList.filter(item => item.isShow && item.fieldType !== '11')
    )
    filedList = filedList.map(item => {
      if (
        item.fieldValue &&
        ['3', '4', '5', '8', '12'].includes(item.fieldType) &&
        !item.systemType
      ) {
        item.fieldValue = JSON.stringify(item.fieldValue)
      }
      return item
    })
    const params: any = {
      ...props.routeData,
      deviceId: getRandomNumber(),
      submitFieldList: filedList
    }
    if (props.routeData.templateType === '1') {
      await EventSubmit(params)
      isResult.value = true
    } else {
      const endTimer = new Date().getTime()
      params.modifiedBy = getRandomNumber()
      params.surveyLogId = props.initData.surveyLogId
      params.surveyId = props.initData.surveyId
      params.answerDate = Math.floor((endTimer - startTime.value) / 1000)
      const result: any = await FormSubmit(params)
      isResult.value = true
      // window.checkShowPoint.postMessage(params.surveyLogId)
      // 提交成功后通知App显示积分动效，参数格式：{"type":1,"point": 积分数值}
      const point = result?.data ?? 0
      const pointData = JSON.stringify({ type: 1, point })
      ;(window as any).Flutter?.postMessage(pointData)
    }
  } catch (error) {
  } finally {
    loading.value = false
  }
}

watch(
  () => data.value.filedList,
  value => {
    data.value.filedList.forEach(item => {
      if (item.displayData.length > 0) {
        item.isShow = item.displayData.some(i => {
          const result = data.value.filedList.find(n => n.id === i.id)
          if (result.fieldValue && result.isShow) {
            const arr = result.fieldValue.map(l => l.optionId) || []
            return arr.length > 0 && containsList(i.value, arr)
          }
          return false
        })
      }
    })
  },
  { deep: true }
)

onMounted(async () => {
  data.value = props.initData
  logicConfigList.value = data.value.filedList
    .filter(item => item.logicConfigList)
    .flatMap(item => item.logicConfigList)
  data.value?.filedList?.forEach(item => {
    item.displayData = []
    item.isShow = true
    logicConfigList.value.forEach(i => {
      if (i.displayFieldIdList.includes(item.id)) {
        item.displayData.push({ id: i.fieldId, value: i.optionIdList })
        item.isShow = false
      }
    })
  })
  startTime.value = new Date().getTime()
})
</script>

<style lang="less" scoped>
.app-form {
  display: flex;
  flex-direction: column;
  height: 100%;
  font-family: 'PingFang SC', 'OpenSans', 'Microsoft YaHei', '宋体', Tahoma, Arial, sans-serif;
  background-color: #fff;
}

.app-form-body {
  flex: 1;
  padding: 16px;
  text-align: left;
  overflow: auto;
  &::-webkit-scrollbar {
    display: none;
  }
  .app-form-banner {
    margin-bottom: 8px;
    .van-image {
      border-radius: 8px;
      overflow: hidden;
    }
  }
  .app-form-title {
    font-size: 18px;
    line-height: 26px;
    font-weight: 600;
    margin-bottom: 8px;
    color: rgba(0, 0, 0, 0.9);
  }
  .app-form-subtitle {
    font-size: 16px;
    line-height: 24px;
    margin-bottom: 8px;
    color: rgba(0, 0, 0, 0.7);
  }
  .form-class {
    .van-field {
      padding: 8px 0;
    }
  }
}
.disclaimer-class {
  color: rgba(0, 0, 0, 0.7);
  text-align: center;
  margin: 16px 0;
}
.app-form-fotter {
  margin: 0 auto;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 10px 16px;
  flex-direction: column;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  :deep(.form-agreement) {
    .van-field {
      padding: 0;
      margin-bottom: 20px;
    }
  }
  .agreement-class {
    width: 100%;
    display: flex;
    text-align: left;
    gap: 4px;
  }
  .submit-button {
    height: 40px;
    line-height: 40px;
    border-radius: 24px;
    border: none;
    background-color: rgb(215, 0, 15);
  }
}
:deep(.field-class) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
:deep(.van-field__word-limit) {
  font-size: 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  padding-bottom: 12px;
  color: rgba(0, 0, 0, 0.5) !important;
}
:deep(.van-cell__title) {
  color: rgba(0, 0, 0, 0.7) !important;
  font-size: 16px;
}
:deep(.van-field__control) {
  font-size: 16px;
  color: rgba(0, 0, 0, 0.7) !important;
  --van-field-input-disabled-text-color: rgba(0, 0, 0, 0.3) !important;
  --van-field-placeholder-text-color: rgba(0, 0, 0, 0.3) !important;
}
:deep(.van-rate__item) {
  margin-right: 20px;
  padding: 0;
  &:last-child {
    margin-right: 0;
  }
}
:deep(.picker-class) {
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  &::after {
    border-bottom: none;
  }
}
:deep(ul) {
  li {
    position: relative;
    padding-left: 16px;
    &::after {
      position: absolute;
      content: '';
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: #505050;
      display: block;
      left: 0;
      top: 10px;
    }
  }
}
:deep(.explaination) {
  padding: 12px 0;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.7);
  border-bottom: 0.5px solid rgba(0, 0, 0, 0.12);
}
:deep(.sort-class) {
  .ant-row {
    line-height: 38px;
    border-radius: 8px;
    div {
      width: 20px;
      height: 20px;
      font-size: 14px;
    }
  }
  .selected-class {
    background-color: #fff;
    border-color: rgb(215, 0, 15);
    div {
      border-color: rgb(215, 0, 15);
      background-color: rgb(215, 0, 15);
    }
  }
}
</style>
